import { useId, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed, Loading, failureText } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { Pagination } from '../../ui/Pagination'
import { Select, type SelectOption } from '../../ui/Select'
import { ExportButton } from '../../ui/ExportButton'
import { PageActions } from '../../app/PageHeader'
import { useSession } from '../auth/session'
import { useStaff } from '../admins/api'
import { Board } from './Board'
import { OrdersTable } from './OrdersTable'
import { StatusDialog } from './StatusDialog'
import {
  useBoard,
  useChangeStatus,
  useExportOrders,
  useOrders,
  type BoardCard,
  type Order,
} from './api'
import {
  EMPTY_FILTERS,
  PAGE_SIZES,
  STATUS_ORDER,
  columnTarget,
  dropHint,
  filtersFromParams,
  filtersToParams,
  isFiltered,
  statusText,
  toOrderQuery,
  type OrderFilters,
  type OrdersTab,
  type PageSize,
} from './model'

const SHIPS: SelectOption<OrderFilters['ship']>[] = [
  { value: 'all', label: 'Любое получение' },
  { value: 'delivery', label: 'Доставка' },
  { value: 'pickup', label: 'Самовывоз' },
]

/** На доске тот же фильтр — сегментами: вариантов три, выбор виден сразу. */
const SEGMENTS: [OrderFilters['ship'], string][] = [
  ['all', 'Все'],
  ['delivery', 'Доставка'],
  ['pickup', 'Самовывоз'],
]

const STATUSES: SelectOption<OrderFilters['status']>[] = [
  { value: 'all', label: 'Все статусы' },
  ...STATUS_ORDER.map((status) => ({ value: status, label: statusText(status) })),
]

export interface OrdersScreenProps {
  onToast: (message: string) => void
}

/**
 * Заказы: доска обработки и список. Доска — рабочий экран смены, список —
 * поиск по всему, что было. Сотруднику аптеки сервер отдаёт только её заказы,
 * сужать выборку на экране не нужно (В-38).
 *
 * Вкладка, фильтры и страница живут в адресе: «Назад» из карточки заказа
 * возвращает туда, откуда её открыли.
 */
export function OrdersScreen({ onToast }: OrdersScreenProps) {
  const { can } = useSession()
  const [params, setParams] = useSearchParams()
  const filters = filtersFromParams(params)
  const query = toOrderQuery(filters)
  const [moving, setMoving] = useState<Order | null>(null)
  const ids = useId()

  const canUpdate = can('orders', 'u')
  const board = useBoard(0, query.ship)
  const list = useOrders(query, filters.tab === 'list', true)
  const staff = useStaff()
  const change = useChangeStatus()
  const exportOrders = useExportOrders()

  const assigneeName = (id: number) => {
    if (!id) return ''
    // Справочник сотрудников может быть закрыт ролью — тогда хотя бы не
    // врём, что сборщика нет.
    return staff.data?.find((a) => a.id === id)?.name ?? 'Сборщик назначен'
  }
  const inWork = (board.data ?? []).reduce((sum, column) => sum + column.total, 0)
  const filtered = isFiltered(filters)

  /**
   * Новый отбор начинается с первой страницы. Листание — отдельный шаг в
   * истории браузера, а правка фильтра заменяет текущий: иначе каждая буква
   * поиска стала бы шагом.
   */
  function set(patch: Partial<OrderFilters>) {
    const paging = 'page' in patch
    setParams(filtersToParams({ ...filters, page: 1, ...patch }), { replace: !paging })
  }

  function reset() {
    set({ q: EMPTY_FILTERS.q, status: EMPTY_FILTERS.status, ship: EMPTY_FILTERS.ship })
  }

  function goTo(page: number) {
    set({ page })
    // Новая страница читается сверху: таблица под фильтрами.
    window.scrollTo({ top: 0 })
  }

  function apply(order: Order, to: Parameters<typeof change.mutate>[0]['to'], cash = false) {
    change.mutate(
      { id: order.id, to, cashCollected: cash },
      {
        onSuccess: (next) => {
          onToast(`№ ${next.number}: ${statusText(next.status).toLowerCase()}`)
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  function drop(card: BoardCard, columnKey: string) {
    const target = columnTarget(columnKey)
    if (!target) {
      onToast(dropHint(card.status, columnKey))
      return
    }
    apply(card, target)
  }

  const tabs: [OrdersTab, string, number | undefined][] = [
    ['board', 'Обработка', board.data ? inWork : undefined],
    ['list', 'Все заказы', list.data?.total],
  ]

  return (
    <>
      {/* Вкладки — главный переключатель экрана, поэтому в строке заголовка
          только выгрузка, и только у списка: доска — рабочий экран смены, а
          не отчёт. */}
      {filters.tab === 'list' ? (
        <PageActions>
          <span className="spacer" />
          <ExportButton prefix="zakazy" load={() => exportOrders(query)} onError={onToast} />
        </PageActions>
      ) : null}
      <div className="ui-tabs ord-tabs" role="tablist" aria-label="Заказы">
        {tabs.map(([tab, title, count]) => (
          <button
            key={tab}
            id={`${ids}-${tab}`}
            type="button"
            role="tab"
            className="ui-tab"
            aria-selected={filters.tab === tab}
            aria-controls={`${ids}-panel`}
            onClick={() => {
              set({ tab })
            }}
          >
            {title}
            {count === undefined ? null : (
              <span
                className="ui-count"
                aria-label={withCountRu(count, ['заказ', 'заказа', 'заказов'])}
              >
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div id={`${ids}-panel`} role="tabpanel" aria-labelledby={`${ids}-${filters.tab}`}>
        {filters.tab === 'board' ? (
          <>
            <div className="ui-toolbar">
              <div className="ui-seg" role="group" aria-label="Способ получения">
                {SEGMENTS.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={filters.ship === value}
                    onClick={() => {
                      set({ ship: value })
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {canUpdate ? (
                <>
                  <span className="spacer" />
                  {/* Подсказка — текстом, не рамкой: это не кнопка. У переноса
                      есть кнопочная замена на самой карточке. */}
                  <span className="ui-hint ord-draghint">
                    Карточку можно перетащить в соседнюю колонку
                  </span>
                </>
              ) : null}
            </div>
            {board.isPending ? <Loading what="доску" /> : null}
            {board.isError ? (
              <Failed
                what="доску"
                error={board.error}
                onRetry={() => {
                  void board.refetch()
                }}
              />
            ) : null}
            {board.data ? (
              <Board
                columns={board.data}
                canUpdate={canUpdate}
                assigneeName={assigneeName}
                onAdvance={(card, step) => {
                  apply(card, step.to, step.cash)
                }}
                onMoveDialog={setMoving}
                onDrop={drop}
              />
            ) : null}
          </>
        ) : (
          <>
            {/* Одна строка управления: поиск и фильтры одной высоты и формы.
                Таблица — отдельной карточкой под ней. */}
            <div className="ui-toolbar" role="search">
              <div className="ui-search">
                <Icon name="i-search" />
                <input
                  className="ui-input"
                  type="search"
                  placeholder="Номер заказа или телефон"
                  aria-label="Поиск заказа"
                  value={filters.q}
                  onChange={(e) => {
                    set({ q: e.target.value })
                  }}
                />
              </div>
              <Select
                label="Статус"
                value={filters.status}
                active={filters.status !== EMPTY_FILTERS.status}
                options={STATUSES}
                onChange={(status) => {
                  set({ status })
                }}
              />
              <Select
                label="Способ получения"
                value={filters.ship}
                active={filters.ship !== EMPTY_FILTERS.ship}
                options={SHIPS}
                onChange={(ship) => {
                  set({ ship })
                }}
              />
              {filtered ? (
                <button type="button" className="ui-btn" data-variant="ghost" onClick={reset}>
                  Сбросить фильтры
                </button>
              ) : null}
            </div>

            <section className="ui-table-card" aria-label="Все заказы">
              {list.isPending ? <TableSkeleton what="заказы" /> : null}
              {list.isError ? (
                <Failed
                  what="заказы"
                  error={list.error}
                  onRetry={() => {
                    void list.refetch()
                  }}
                />
              ) : null}
              {list.data ? (
                <>
                  <OrdersTable
                    orders={list.data.items}
                    filtered={filtered}
                    page={filters.page}
                    busy={list.isPlaceholderData}
                    onReset={reset}
                    onFirstPage={() => {
                      goTo(1)
                    }}
                  />
                  <Pagination
                    label="Страницы заказов"
                    page={filters.page}
                    pageSize={filters.size}
                    total={list.data.total}
                    sizes={PAGE_SIZES}
                    busy={list.isPlaceholderData}
                    onPage={goTo}
                    onPageSize={(size) => {
                      set({ size: size as PageSize })
                    }}
                  />
                </>
              ) : null}
            </section>
          </>
        )}
      </div>

      {moving ? (
        <StatusDialog
          order={moving}
          onPick={(to, cash) => {
            apply(moving, to, cash)
            setMoving(null)
          }}
          onClose={() => {
            setMoving(null)
          }}
        />
      ) : null}
    </>
  )
}
